import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FederatedInfoResult } from '@shared/federatedinforesult';
import { BehaviorSubject, catchError, filter, map, Observable, of, pairwise, retry, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IData as IDataTopbar } from '../../topbar/topbar.component';
import { SessionStorageService } from './storage.service';

export interface IModelMetaData {
	createdAt?: number;
	createdBy?: string;
	updatedAt?: number;
	updatedBy?: string;
	version?: number;
}

export interface IModelBase extends IModelMetaData {
	id?: string;
}

export interface IEngine extends IModelBase {
	name: string;
}

export interface IOrganization extends IModelBase {
	name: string;
}

export interface IAccount extends IModelBase {
	name: string;
	organization: IOrganization;
	// language: string;
	// currency: string;
	defaultEngine: string | null;
	allowedIPs: string[];
}

type IPermissionList = string[];

export interface IUserAccessResult extends IModelBase {
	result: {
		id: string;
		permissions: IPermissionList;
		organizations: {
			id: string;
			name: string;
			permissions: IPermissionList;
			accounts: {
				id: string;
				name: string;
				defaultEngine: string | null;
				permissions: IPermissionList;
				allowedIPs: string[];
			}[];
		}[];
	};
}

/** The shape of `ua.result` once it's cached verbatim in `sessionStorage['User.AccessMap']`. */
export type IAccessMap = IUserAccessResult['result'];

export interface IUserAccess {
	id: string;
	name: string;
	email: string;
	adminuser: boolean;
	reportmanager: boolean;
	interval: number;
	permissionCodes: string[];
	defaultViewId?: string;
	defaultViewType?: string;
	grantType: 'account' | 'managed';
}

export interface IUsers {
	result: IUser[];
}

export interface IUserResult {
	result: IUser;
}

export interface IUser extends IModelBase {
	email: string;
	status: string;
	name: string;
	defaultAccount: string;
}

export interface IFederatedDomainsResult {
	result: IFederatedDomain[];
}

export interface IFederatedDomain extends IModelBase {
	emailDomain: string;
}

@Injectable({
	providedIn: 'root',
})
export class UserService {
	public accountsSubject$: BehaviorSubject<IAccount[]>;
	public accounts$: Observable<IAccount[]>;

	private accountSubject$: BehaviorSubject<IAccount | null>;
	public account$: Observable<IAccount | null>;

	// Emits only on a real switch between two accounts (not the initial value, login or logout).
	public accountChanged$: Observable<IAccount | null>;

	private impersonateUserSubject$: BehaviorSubject<IUserAccess | null>;
	public impersonateUser$: Observable<IUserAccess | null>;

	public topbarAccounts$: Observable<IDataTopbar[]>;

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
	) {
		this.accountsSubject$ = new BehaviorSubject<IAccount[]>([]);
		this.accounts$ = this.accountsSubject$.asObservable();

		this.accountSubject$ = new BehaviorSubject<IAccount | null>(null);
		this.account$ = this.accountSubject$.asObservable();

		this.accountChanged$ = this.account$.pipe(
			pairwise(),
			filter(([prev, curr]) => !!prev && !!curr && prev.id !== curr.id),
			map(([, curr]) => curr),
		);

		this.impersonateUserSubject$ = new BehaviorSubject<IUserAccess | null>(null);
		this.impersonateUser$ = this.impersonateUserSubject$.asObservable();

		// if only one account, use that account
		this.accounts$.pipe(filter((v) => v.length === 1)).subscribe((v) => {
			this.account = v[0];
		});
	}

	public get account(): IAccount | null {
		return this.accountSubject$.value;
	}

	public set account(v: IAccount | null) {
		if (this.account?.id !== v?.id) {
			this.accountSubject$.next(v);

			if (v) {
				this.sessionStorageService.set('User.Account', JSON.stringify(v));
			} else {
				this.sessionStorageService.remove('User.Account');
			}
		}
	}

	public get impersonateUser(): IUserAccess | null {
		return this.impersonateUserSubject$.value || null;
	}

	public set impersonateUser(v: IUserAccess | null) {
		if (this.impersonateUser?.id !== v?.id) {
			this.impersonateUserSubject$.next(v);
		}
	}

	public getHeaders(): HttpHeaders {
		const headers = new HttpHeaders({
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': this.account.id,
		});
		return headers;
	}

	public deleteIAMUser(userId: string): Observable<string> {
		const account = this.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		return this.http
			.post<string>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}/users/${userId}/deleteUser`, '{}', {
				headers,
			})
			.pipe(
				retry(1),
				tap(() => this.clearUsersCache(account)),
				catchError(this.handleError),
			);
	}

	public fetchAccountsObs(): Observable<IUserAccessResult> {
		let headers = {};
		if (this.sessionStorageService.get('User.InviteToken')) {
			headers = {
				Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
				'x-invite-token': this.sessionStorageService.get('User.InviteToken').toString(),
			};
		} else {
			headers = {
				Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			};
		}
		return this.http
			.get<IUserAccessResult>(`${environment.awsEnvUri}/iam/me/access`, {
				headers,
			})
			.pipe(retry(1));
	}

	public fetchUsers(account?: IAccount): Observable<IUser[]> {
		let offset: number = 0;
		const userAccount: IAccount = account != null ? account : (JSON.parse(this.sessionStorageService.get('User.Account')) as IAccount);
		const organizationId: string = userAccount.organization.id;
		const accountId: string = userAccount.id;

		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		const users: IUser[] = [];

		const fetchNextBatch = (): Observable<IUser[]> => {
			const apiUrl = `${environment.awsEnvUri}/iam/organizations/${organizationId}/accounts/${accountId}/users?limit=60&offset=${offset}`;
			return this.http.get<IUsers>(apiUrl, { headers }).pipe(
				switchMap((response: IUsers) => {
					users.push(...response.result);
					if (response.result.length < 60) {
						return of(users);
					} else {
						offset += 60;
						return fetchNextBatch();
					}
				}),
				retry(1),
			);
		};

		return fetchNextBatch().pipe(catchError(this.handleError));
	}

	public clearUsersCache(_account?: IAccount): void {
		// fetchUsers no longer caches IAM users; keep this as a no-op for existing callers.
	}

	public fetchUserPermissionsByEmail(email: string): Observable<IUsers> {
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		return this.http
			.get<IUsers>(`${environment.awsEnvUri}/iam/users?email=${email}`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public fetchMe(): Observable<IUserResult> {
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		return this.http
			.get<IUserResult>(`${environment.awsEnvUri}/iam/me`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getFederatedInfo(email: string): Observable<FederatedInfoResult> {
		let domain = '';
		domain = email;
		domain = domain.split('@', 2)[1];
		const federatedDomainURL = `${environment.awsEnvUri}/iam/federateddomainsinfo/${domain}`;
		return this.http.get<any>(federatedDomainURL).pipe(retry(1), catchError(this.handleError));
	}

	public getFederatedDomains(): Observable<IFederatedDomainsResult> {
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		const federatedDomainURL = `${environment.awsEnvUri}/iam/federateddomains`;
		return this.http
			.get<any>(federatedDomainURL, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public sendInviteToken(userId: string): void {
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		this.http
			.post<string>(`${environment.awsEnvUri}/iam/users/${userId}/sendinvitetoken`, '{}', {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError))
			.subscribe((data) => {
				console.log(`Invite sent to ${data.result.email}`);
			});
	}

	public sendInviteTokenByAccount(userId: string): void {
		const account = this.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};

		this.http
			.post<string>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}/users/${userId}/sendinvitetoken`, '{}', {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError))
			.subscribe((data) => {
				console.log(`Invite sent to ${data.result.email}`);
			});
	}

	public handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			console.log(errorMessage);
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}
